/**
 * <s-tabs> — accessible tab set built from caller markup.
 * Tabs are buttons with role="tab", data-tab="<index>", commandfor="<s-tabs id>"
 * and command="--select"; panels carry role="tabpanel" in the same order.
 * Selecting a tab opens the first <details> of its panel when the panel is an
 * accordion, so a freshly selected FAQ category always shows an answer.
 */
class STabs extends HTMLElement {
  #ac;

  connectedCallback() {
    this.#ac = new AbortController();
    const opts = { signal: this.#ac.signal };

    this.addEventListener('command', (event) => {
      if (event.command !== '--select') return;
      this.select(Number.parseInt(event.source?.dataset.tab || '0', 10), true);
    }, opts);

    this.addEventListener('keydown', (event) => {
      const tab = event.target.closest?.('[role="tab"]');
      if (!tab || !['ArrowRight', 'ArrowLeft', 'Home', 'End'].includes(event.key)) return;
      const tabs = this.#tabs;
      const current = tabs.indexOf(tab);
      let next = current;
      if (event.key === 'ArrowRight') next = (current + 1) % tabs.length;
      if (event.key === 'ArrowLeft') next = (current - 1 + tabs.length) % tabs.length;
      if (event.key === 'Home') next = 0;
      if (event.key === 'End') next = tabs.length - 1;
      event.preventDefault();
      this.select(next, true);
      tabs[next]?.focus();
    }, opts);
  }

  disconnectedCallback() {
    this.#ac?.abort();
  }

  get #tabs() {
    return [...this.querySelectorAll('[role="tab"]')].filter((tab) => tab.closest('s-tabs') === this);
  }

  get #panels() {
    return [...this.querySelectorAll('[role="tabpanel"]')].filter((panel) => panel.closest('s-tabs') === this);
  }

  select(index, openFirst = false) {
    this.#tabs.forEach((tab, i) => {
      const selected = i === index;
      tab.setAttribute('aria-selected', String(selected));
      tab.tabIndex = selected ? 0 : -1;
      tab.toggleAttribute('data-active', selected);
    });
    this.#panels.forEach((panel, i) => {
      panel.hidden = i !== index;
      if (i === index && openFirst) {
        const first = panel.querySelector('details');
        if (first) first.open = true;
      }
    });
    this.dispatchEvent(new CustomEvent('s-tabs:change', { bubbles: true, detail: { index } }));
  }
}

if (!customElements.get('s-tabs')) {
  customElements.define('s-tabs', STabs);
}
